import { Icon } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import { useState } from "react";

import { LeadV2Status } from "../../api/generated";
import {
  ApplicationStartedIcon,
  BaseCheckCircleIcon,
  HelpIcon,
} from "../../Icons";
import { MobileStatusModal } from "../../LeadPage/LeadsTable/MobileStatusModal";
import { TooltipWrapper } from "../../Tooltip";
import { Typo } from "../../Typo";
import { RecentLeadStatusModal } from "./RecentLeadStatusModal";
import { RecentLeadsStatusTooltip } from "./RecentLeadStatusTooltip";
import { LeadRawStatus } from "./types";

export const RecentLeadStatus = ({
  id,
  status,
  displayValue,
  toolTipDescription = "",
  showTooltip = true,
  descriptionMapping,
  isMobile,
  onTooltipHover,
}: {
  id?: string;
  status: LeadV2Status;
  displayValue: string;
  toolTipDescription?: string;
  showTooltip?: boolean;
  isMobile?: boolean;
  descriptionMapping: Map<string, string>;
  onTooltipHover?: () => void;
}) => {
  const [mobileModalStatusVisible, setMobileModalStatusVisible] =
    useState(false);

  const statusStyle = getNewLeadStatusStyle(status);
  const toolTipText = descriptionMapping.get(status) || toolTipDescription;

  if (isMobile) {
    return (
      <div
        className="flex flex-row items-center justify-start gap-2 md:justify-end"
        data-testid={status}
      >
        <MobileStatusModal
          isVisible={mobileModalStatusVisible}
          close={() => setMobileModalStatusVisible(false)}
          status={displayValue}
          statusDescription={toolTipText}
        />
        <div
          className={clsx([
            statusStyle.bgColor,
            statusStyle.txtColor,
            "flex flex-shrink-0 flex-row items-center justify-start rounded-md px-2 py-1 hover:opacity-100",
          ])}
        >
          <div className="pe-1">{statusStyle.icon}</div>
          <Typo element="p" type="labelMD" textColor="text-inherit">
            {displayValue}
          </Typo>
        </div>
        <div
          data-testid="recent-lead-status-tooltip-icon"
          className="cursor-pointer text-icon-tertiary"
          onClick={() => {
            setMobileModalStatusVisible(true);
            onTooltipHover?.();
          }}
        >
          <Icon icon="help" size="small" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-row items-center justify-end gap-2"
      data-testid={status}
    >
      <TooltipWrapper
        id={`tooltip-${id}`}
        color="blue"
        tooltipPlacement="top"
        tooltipTextAlign="text-center"
        text={showTooltip ? toolTipText : ""}
      >
        <div
          data-testid="recent-lead-status-tooltip-wrapper"
          className={clsx([
            statusStyle.bgColor,
            statusStyle.txtColor,
            "flex flex-shrink-0 flex-row items-center justify-start gap-1 rounded-md px-2 py-1 hover:opacity-100",
          ])}
          onMouseEnter={() => {
            onTooltipHover?.();
          }}
        >
          <div className="pe-1">{statusStyle.icon}</div>
          <Typo
            element="p"
            type="labelMD"
            textColor="text-inherit text-nowrap overflow-hidden text-ellipsis"
          >
            {displayValue}
          </Typo>
        </div>
      </TooltipWrapper>
    </div>
  );
};

const getNewLeadStatusStyle = (status: LeadV2Status) => {
  switch (status) {
    // New Lead Statuses styling
    case LeadV2Status.IncompleteProfile:
      return {
        icon: <Icon icon="clock" size="small" />,
        bgColor: "bg-surface-secondary",
        hoverOpacity: "hover:opacity-5",
        txtColor: "text-text-primary",
      };
    case LeadV2Status.CompletingProfile:
      return {
        icon: (
          <ApplicationStartedIcon data-testid="application-started-icon-completing-profile" />
        ),
        bgColor: "bg-surface-tags-blue",
        hoverOpacity: "hover:opacity-5",
        txtColor: "text-text-blue",
      };
    case LeadV2Status.ReviewingSuitability:
      return {
        icon: (
          <ApplicationStartedIcon data-testid="application-started-icon-reviewing-suitability" />
        ),
        bgColor: "bg-surface-tags-blue",
        hoverOpacity: "hover:opacity-5",
        txtColor: "text-text-blue",
      };
    case LeadV2Status.IdentifyVerification:
      return {
        icon: (
          <ApplicationStartedIcon data-testid="application-started-icon-identify-verification" />
        ),
        bgColor: "bg-surface-brand-subtle",
        hoverOpacity: "hover:opacity-5",
        txtColor: "text-text-blue",
      };
    case LeadV2Status.Approved:
      return {
        icon: <Icon icon="clock" size="small" />,
        bgColor: "bg-surface-tags-green",
        hoverOpacity: "hover:opacity-5",
        txtColor: "text-text-success",
      };
    case LeadV2Status.VerifyingIdentity:
      return {
        icon: <Icon icon="clock" size="small" />,
        bgColor: "bg-surface-accent-amber-subtler",
        hoverOpacity: "hover:opacity-5",
        txtColor: "text-text-warning-bold",
      };
    default:
      return {
        icon: <Icon icon="slash-circle" size="small" />,
        bgColor: "bg-surface-disabled",
        txtColor: "text-text-tertiary",
      };
  }
};

export const RecentLeadStatusV2 = ({
  id,
  rawStatus,
  isMobile,
  displayValue,
  onTooltipHover,
}: {
  id?: string;
  rawStatus: string;
  isMobile?: boolean;
  displayValue?: string;
  onTooltipHover?: () => void;
}) => {
  const [mobileModalStatusVisible, setMobileModalStatusVisible] =
    useState(false);

  if (isMobile) {
    return (
      <div
        className="flex flex-row items-center justify-end gap-2"
        data-testid={rawStatus}
      >
        <RecentLeadStatusModal
          isVisible={mobileModalStatusVisible}
          close={() => setMobileModalStatusVisible(false)}
          status={rawStatus}
        />
        <button
          data-testid="recent-lead-status-tooltip-wrapper"
          className="flex flex-shrink-0 flex-row items-center justify-start gap-[2px] rounded-full border border-stroke-primary bg-surface-primary px-2 py-1 hover:opacity-100"
          onClick={() => {
            setMobileModalStatusVisible(true);
            onTooltipHover?.();
          }}
        >
          <RecentLeadsIcon rawStatus={rawStatus} />
          <Typo
            element="p"
            type="labelXS"
            textColor="text-inherit text-nowrap overflow-hidden text-ellipsis text-underline-offset-2 px-[2px] lg:px-[4px]"
          >
            {displayValue}
          </Typo>
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex flex-row items-center justify-end gap-2"
      data-testid={rawStatus}
    >
      <RecentLeadsStatusTooltip id={id} status={rawStatus}>
        <div
          data-testid="recent-lead-status-tooltip-wrapper"
          className="flex flex-shrink-0 flex-row items-center justify-start gap-1 rounded-full border border-stroke-primary bg-surface-primary px-2 py-1 hover:opacity-100"
          onMouseEnter={() => {
            onTooltipHover?.();
          }}
        >
          <RecentLeadsIcon rawStatus={rawStatus} />
          <Typo
            element="p"
            type="labelSM"
            textColor="text-inherit text-nowrap overflow-hidden text-ellipsis"
          >
            {displayValue}
          </Typo>
        </div>
      </RecentLeadsStatusTooltip>
    </div>
  );
};

const RecentLeadsIcon = ({ rawStatus }: { rawStatus: string }) => {
  switch (rawStatus.toLowerCase()) {
    // New Lead Statuses styling
    case LeadRawStatus.Demo:
      return (
        <RecentLeadsLevelIcon
          data-testid="recent-leads-level-icon-demo"
          level={1}
        />
      );
    case LeadRawStatus.Lead_1:
      return (
        <RecentLeadsLevelIcon
          data-testid="recent-leads-level-icon-lead-1"
          level={2}
        />
      );
    case LeadRawStatus.Lead_2:
      return (
        <RecentLeadsLevelIcon
          data-testid="recent-leads-level-icon-lead-2"
          level={3}
        />
      );
    case LeadRawStatus.Lead_4:
      return (
        <RecentLeadsLevelIcon
          data-testid="recent-leads-level-icon-lead-4"
          level={4}
        />
      );
    case LeadRawStatus.Lead_5:
      return (
        <RecentLeadsLevelIcon
          data-testid="recent-leads-level-icon-lead-5"
          level={5}
        />
      );
    case LeadRawStatus.Approved:
      return (
        <BaseCheckCircleIcon data-testid="recent-leads-level-icon-approved" />
      );
    default:
      return <HelpIcon />;
  }
};

/**
 * displays bar icons for demo and lead status
 * @param param0
 * @returns
 */
const RecentLeadsLevelIcon = ({
  level,
  "data-testid": dataTestId,
}: {
  level: number;
  "data-testid"?: string;
}) => {
  return (
    <div
      className="flex flex-row items-stretch justify-center gap-[1px] lg:gap-[2px]"
      data-testid={dataTestId}
    >
      {Array(5)
        .fill(0)
        .map((_, index) => (
          <div
            key={index}
            className={clsx([
              "h-[12px] w-[2.4px] rounded-lg lg:h-[14px] lg:w-[3px]",
              index < level ? "bg-icon-brand-default" : "bg-surface-tonal-bold",
            ])}
          />
        ))}
    </div>
  );
};
